import { useActionSheet } from '@expo/react-native-action-sheet';
import { format } from 'date-fns';
import { Image } from 'expo-image';
import { router, Stack, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Linking, Platform, Pressable, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useReservation } from '@/api/reservations/use-reservation';
import BackButton from '@/components/back-button';
import ContactSupport from '@/components/contact-support';
import Footer from '@/components/footer';
import ListItemStatus from '@/components/list-item-status';
import { MapView } from '@/components/map-view';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import type { CarMarkerProps } from '@/components/search/car-marker';
import { getCarImages, openAddressInMaps, snakeToTitleCase } from '@/core';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Car as CarIcon } from '@/ui/icons';
import { Call } from '@/ui/icons/call';
import { FlagEmpty } from '@/ui/icons/flag-empty';
import { Hourglass } from '@/ui/icons/hourglass';
import { License } from '@/ui/icons/license';
import { Loader } from '@/ui/loader';
import { MenuItem } from '@/ui/menu-item';

export default function CarDetails() {
  const { styles, theme } = useStyles(stylesheet);
  const { id } = useLocalSearchParams();
  const { showActionSheetWithOptions } = useActionSheet();

  const { data: resData, isPending: isFetchingReservation } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const markers = React.useMemo(() => {
    if (!resData || !resData.car) return [];

    const car = resData.car;

    let ms: CarMarkerProps[] = [];

    ms = [
      ...ms,
      {
        latitude: car.carState.lat,
        longitude: car.carState.lon,
        icon: (
          <Icon
            type="naked"
            icon={<CarIcon />}
            fillColor={theme.colors.icon.color}
            width={20}
            height={20}
            style={{ borderColor: 'red', borderWidth: 1 }}
          />
        ),
      },
    ];

    if (resData.customPickupLocation) {
      ms = [
        ...ms,
        {
          latitude: resData.pickupLocationLatitude,
          longitude: resData.pickupLocationLongitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagEmpty />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
        },
      ];
    } else {
      ms = [
        ...ms,
        {
          latitude: resData.car.defaultLocation.latitude,
          longitude: resData.car.defaultLocation.longitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagEmpty />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
        },
      ];
    }

    return ms;
  }, [resData]);

  const defaultCenter = React.useMemo(() => {
    if (!resData || !resData.car) return;

    const car = resData.car;

    return {
      latitude: car.carState.lat,
      longitude: car.carState.lon,
    };
  }, [resData]);

  if (isFetchingReservation) {
    return (
      <View style={{ flexGrow: 1, backgroundColor: theme.colors.background_1 }}>
        <Loader />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {Platform.OS === 'web' &&
      ['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: 'Car Details',
            headerTitle: 'Car Details',
            headerLeft: () => <BackButton />,
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
          }}
        />
      )}

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />
          </View>
        ) : null}

        <View style={{ flexGrow: 1 }}>
          <View style={{ gap: 24 }}>
            <View>
              <Image
                source={{
                  uri:
                    resData.car && getCarImages(resData.car.images).length > 0
                      ? getCarImages(resData.car.images)[0]?.url
                      : '',
                }}
                style={{ width: '100%', aspectRatio: 16 / 9 }}
                contentFit="contain"
              />
            </View>

            {/* {resData.controlPanelState === 'disabled' ? (
              <Pressable
                style={{
                  backgroundColor: theme.colors.card.disabled.backgroundColor,
                  borderColor: theme.colors.card.disabled.borderColor,
                  borderWidth: 1,
                  borderRadius: 16,
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingLeft: 8,
                  paddingRight: 8,
                  paddingTop: 16,
                  paddingBottom: 16,
                  gap: 8,
                }}
                disabled
              >
                <Icon
                  type="dressed"
                  icon={<ControlPanel />}
                  width={18}
                  height={18}
                  fillColor={theme.colors.text.secondary50}
                />

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary50 }}
                >
                  Check-in first to unlock control panel
                </Text>
              </Pressable>
            ) : null}

            {resData.controlPanelState === 'enabled' ? (
              <MenuItem
                leftIcon={<ControlPanel />}
                leftIconFillColor={theme.colors.chip.brand.color}
                rightIcon={<ChevronRight />}
                rightIconFillColor={theme.colors.icon.color}
                tx="tripDetails.controlPanelTitle"
                onPress={() => {
                  router.navigate(`/trips/${id}/car-control-panel`);
                }}
                style={{
                  width: '100%',
                  backgroundColor: 'rgba(225, 92, 247, 0.2)',
                  borderColor: 'rgba(225, 92, 247, 0.2)',
                  borderWidth: 1,
                }}
              />
            ) : null} */}

            <View
              style={{
                gap: 16,
              }}
            >
              <View style={{ gap: 8 }}>
                <Text type="title2">
                  {resData.customPickupLocation
                    ? 'Delivery location'
                    : 'Pickup location'}
                </Text>

                <Button
                  type="link"
                  onPress={() => {
                    openAddressInMaps(
                      resData.customPickupLocation ||
                        resData.pickupLocation.address
                    );
                  }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    paddingTop: 0,
                    paddingRight: 0,
                    paddingBottom: 0,
                    paddingLeft: 0,
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{
                      textDecorationLine: 'underline',
                      color: theme.colors.text.secondary,
                    }}
                  >
                    {resData.customPickupLocation ||
                      resData.pickupLocation.address}
                  </Text>
                </Button>
              </View>

              <View
                style={{
                  width: '100%',
                  height: 300,
                  borderRadius: 16,
                  overflow: 'hidden',
                  gap: 16,
                }}
              >
                <MapView
                  polylines={[]}
                  markers={markers}
                  defaultCenter={defaultCenter}
                  containerStyle={{ borderRadius: 16 }}
                  fitBounds
                  altitude={3000}
                  zoom={16}
                  showsUserLocation={false}
                />
              </View>

              <View>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Data could take 15 minutes to update.
                </Text>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`Last updated: ${format(
                    new Date(resData.car.carState.since),
                    'MM/dd/yyyy hh:mm a'
                  )}`}
                </Text>
              </View>
            </View>

            <View
              style={{
                height: 1,
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />

            <View style={{ gap: 16 }}>
              <Text type="title2">
                {resData.customPickupLocation
                  ? 'Delivery Details'
                  : 'Pickup Instructions'}
              </Text>

              {['rental', 'cleaning', 'billable', 'completed'].includes(
                resData.status
              ) ? (
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View>
                    <Icon
                      type="dressed"
                      icon={<License />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                      style={{ width: 40, height: 40 }}
                    />
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text type="bodyRegular">
                      {`License plate: ${snakeToTitleCase(
                        resData.car.licensePlateState
                      )} | ${resData.car.licensePlate}`}
                    </Text>
                  </View>
                </View>
              ) : null}

              {['rental', 'cleaning'].includes(resData.status) ? (
                <Pressable
                  onPress={() => {
                    const phoneNumber = resData?.car.ownerPhone;
                    if (phoneNumber) {
                      showActionSheetWithOptions(
                        {
                          options: ['Call', 'Text', 'Cancel'],
                          cancelButtonIndex: 2,
                        },
                        (buttonIndex) => {
                          if (buttonIndex === 0) {
                            Linking.openURL(`tel:${phoneNumber}`);
                          } else if (buttonIndex === 1) {
                            Linking.openURL(`sms:${phoneNumber}`);
                          }
                        }
                      );
                    }
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <View>
                      <Icon
                        type="dressed"
                        icon={<Call />}
                        fillColor={theme.colors.icon.color}
                        width={25}
                        height={25}
                        style={{ width: 40, height: 40 }}
                      />
                    </View>

                    <View style={{ flex: 1 }}>
                      <Text type="bodyRegular">
                        {`Agent's phone: ${resData?.car.ownerPhone}`}
                      </Text>
                    </View>
                  </View>
                </Pressable>
              ) : null}

              <View>
                {resData.customPickupLocation ? (
                  <MenuItem
                    leftIcon={<Call />}
                    leftIconFillColor={theme.colors.icon.color}
                    tx="carDetails.deliveryTitle"
                    tx2="carDetails.deliveryBody"
                    values={{ phone: resData.car.ownerPhone }}
                    style={{ width: '100%' }}
                    disabled
                  />
                ) : (
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {resData.car.defaultLocation.pickupInstructions}
                  </Text>
                )}
              </View>

              {resData.status === 'rental' && !resData.isCheckinComplete ? (
                <ListItemStatus
                  icon={
                    <Icon
                      type="naked"
                      icon={<Hourglass />}
                      fillColor={theme.colors.icon.color}
                      width={14}
                      height={14}
                    />
                  }
                  text={'Check in'}
                  buttonText="Start"
                  onPress={() => {
                    router.push(`/trips/${resData.id}/checkin`);
                  }}
                />
              ) : null}
            </View>

            <View
              style={{
                height: 1,
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />

            <View>
              <Text type="bodyRegular">Need to make changes?</Text>
              <Text type="bodyRegular">Please contact Customer support.</Text>
            </View>

            <ContactSupport />
          </View>
        </View>
      </ScrollView>

      <Footer />
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    paddingTop: 24,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    height: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 600,
    },
    alignSelf: 'center',
    gap: 24,
    paddingBottom: 20,
  },
}));
